'use client';

import type React from 'react';

import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';

interface MinimalVideoPlayerProps {
  videoSrc: string;
  thumbnailSrc: string;
  title?: string;
  className?: string;
  autoplay?: boolean;
}

export default function MinimalVideoPlayer({
  videoSrc,
  thumbnailSrc,
  title,
  className = '',
  autoplay = false,
}: MinimalVideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(autoplay);
  const [isControlsVisible, setIsControlsVisible] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Set up intersection observer for scroll-to-view autoplay
  useEffect(() => {
    if (!autoplay) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.5, // Trigger when at least 50% of the player is visible
      },
    );

    if (playerRef.current) {
      observerRef.current.observe(playerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [autoplay]);

  // Handle autoplay when scrolled into view
  useEffect(() => {
    if (autoplay && isInView && !isPlaying && videoRef.current) {
      setIsLoaded(true);
      setIsMuted(true);

      // Small delay to ensure the video is ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.muted = true;
          videoRef.current.play().catch((error) => {
            console.warn('Autoplay was prevented:', error);
          });
          setIsPlaying(true);
        }
      }, 100);
    }
  }, [autoplay, isInView, isPlaying]);

  // Initialize video
  useEffect(() => {
    if (autoplay) {
      setIsLoaded(true);
    }
  }, [autoplay]);

  // Handle play/pause
  const togglePlay = () => {
    if (!isLoaded) {
      setIsLoaded(true);
    }

    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch((error) => {
          // Handle autoplay policy restrictions
          console.warn('Playback was prevented:', error);
          setIsMuted(true);
          if (videoRef.current) {
            videoRef.current.muted = true;
            videoRef.current
              .play()
              .catch((e) => console.error("Still couldn't play:", e));
          }
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle video time update
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      setCurrentTime(currentTime);
      setProgress((currentTime / duration) * 100);
    }
  };

  // Handle video metadata loaded
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);

      if (autoplay && isInView) {
        videoRef.current.muted = true;
        videoRef.current.play().catch((error) => {
          console.warn('Autoplay was prevented:', error);
        });
      }
    }
  };

  // Handle progress bar click
  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (progressBarRef.current && videoRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * videoRef.current.duration;
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (playerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        playerRef.current.requestFullscreen();
      }
    }
  };

  // Format time (seconds to MM:SS)
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Show/hide controls on mouse movement
  const handleMouseMove = () => {
    setIsControlsVisible(true);

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setIsControlsVisible(false);
      }
    }, 3000);
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  return (
    // biome-ignore lint/nursery/noStaticElementInteractions: <explanation>
    <div
      ref={playerRef}
      className={`relative overflow-hidden rounded-lg aspect-video bg-black ${className}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setIsControlsVisible(false)}
    >
      {/* Thumbnail Image (shown until video is loaded) */}
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Image
            src={thumbnailSrc || '/placeholder.svg'}
            alt={title || 'Video thumbnail'}
            fill
            priority
            className="object-cover"
          />
          <button
            type="button"
            onClick={togglePlay}
            className="absolute flex items-center justify-center size-16 text-white bg-[#4d2ef5] rounded-full hover:bg-[#3f25cc] transition-transform hover:scale-105 z-10"
            aria-label="Play video"
          >
            <Play className="size-8 fill-current" />
          </button>
        </div>
      )}

      {/* Video Element */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <video
        ref={videoRef}
        className={`size-full object-contain ${!isLoaded ? 'hidden' : ''}`}
        preload="metadata"
        playsInline
        muted={isMuted}
        onClick={togglePlay}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
      >
        {isLoaded && <source src={videoSrc} type="video/mp4" />}
        Your browser does not support the video tag.
      </video>

      {/* Minimal Controls (visible on hover or when paused) */}
      <div
        className={`absolute inset-0 flex flex-col justify-end transition-opacity duration-300 ${
          isControlsVisible || !isPlaying ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Progress Bar */}
        {/* biome-ignore lint/nursery/noStaticElementInteractions: <explanation> */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
        <div
          ref={progressBarRef}
          className="h-1 w-full bg-gray-800/50 cursor-pointer"
          onClick={handleProgressBarClick}
        >
          <div
            className="h-full bg-[#4d2ef5]"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between p-3 bg-gradient-to-t from-black/60 to-transparent">
          <div className="flex items-center space-x-3">
            {/* Play/Pause Button */}
            <button
              type="button"
              onClick={togglePlay}
              className="text-white hover:text-[#4d2ef5] transition-colors"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <Pause className="size-5" />
              ) : (
                <Play className="size-5" />
              )}
            </button>

            {/* Volume Control */}
            <button
              type="button"
              onClick={toggleMute}
              className="text-white hover:text-[#4d2ef5] transition-colors"
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <VolumeX className="size-5" />
              ) : (
                <Volume2 className="size-5" />
              )}
            </button>

            {/* Time Display */}
            <div className="text-white text-xs">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          {/* Fullscreen Button */}
          <button
            type="button"
            onClick={toggleFullscreen}
            className="text-white hover:text-[#4d2ef5] transition-colors"
            aria-label="Toggle fullscreen"
          >
            <Maximize className="size-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
